import { StoryGroup } from "@/@types/stories.types";
import { useGetStoriesQuery } from "@/queries/useGetStoriesQuery";
import { useCallback, useEffect, useMemo, useState } from "react";

const VIEWED_STORIES_KEY = "viewed_stories";

// Телефоны для тестирования (пока API не поддерживает allowed_phones)
const ALLOWED_PHONES = import.meta.env.VITE_STORIES_ALLOWED_PHONES
  ? import.meta.env.VITE_STORIES_ALLOWED_PHONES.split(",")
  : [];

function normalizePhone(phone: string): string {
  return phone.replace(/\D/g, "").replace(/^8/, "7");
}

function isPhoneAllowed(userPhone: string, allowedPhones: string[]): boolean {
  if (allowedPhones.length === 0) return true;
  const normalizedUserPhone = normalizePhone(userPhone);
  return allowedPhones.some((allowed) => normalizePhone(allowed) === normalizedUserPhone);
}

interface UseStoriesOptions {
  userPhone: string;
  branchId?: string;
}

export function useStories({ userPhone, branchId }: UseStoriesOptions) {
  const { data: storyGroups = [], isLoading } = useGetStoriesQuery({ branchId });
  const [viewedGroups, setViewedGroups] = useState<Set<string>>(new Set());
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);

  const isVisible = useMemo(() => {
    if (!userPhone) return false;
    if (storyGroups.length === 0 && !isLoading) return false;
    return isPhoneAllowed(userPhone, ALLOWED_PHONES);
  }, [userPhone, storyGroups.length, isLoading]);

  const filteredStoryGroups: StoryGroup[] = useMemo(() => {
    return isVisible ? storyGroups : [];
  }, [isVisible, storyGroups]);

  useEffect(() => {
    const saved = localStorage.getItem(VIEWED_STORIES_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setViewedGroups(new Set(parsed));
      } catch {
        // ignore invalid data
      }
    }
  }, []);

  const markAsViewed = useCallback((groupId: string) => {
    setViewedGroups((prev) => {
      const next = new Set(prev);
      next.add(groupId);
      localStorage.setItem(VIEWED_STORIES_KEY, JSON.stringify([...next]));
      return next;
    });
  }, []);

  const openViewer = useCallback((groupIndex: number) => {
    setActiveGroupIndex(groupIndex);
    setIsViewerOpen(true);
  }, []);

  const closeViewer = useCallback(() => {
    setIsViewerOpen(false);
  }, []);

  return {
    isVisible,
    isLoading,
    storyGroups: filteredStoryGroups,
    viewedGroups,
    isViewerOpen,
    activeGroupIndex,
    openViewer,
    closeViewer,
    markAsViewed,
  };
}
